import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { AuthService } from "../core/auth.service";
import { PasskeyService } from "../core/passkey.service";

@Component({
  selector: "app-login-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule],
  templateUrl: "./login-page.component.html",
  styleUrls: ["./login-page.component.scss"],
})
export class LoginPageComponent {
  private readonly auth = inject(AuthService);
  private readonly passkey = inject(PasskeyService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  email = "";
  password = "";
  error = signal<string | null>(null);
  loading = signal(false);
  passkeyLoading = signal(false);
  passkeyVisible = signal(false);

  async submit() {
    this.error.set(null);
    this.loading.set(true);
    try {
      await this.auth.login(this.email, this.password);
      this.auth.touch();
      this.router.navigate([this.redirectTarget()]);
    } catch (e) {
      this.error.set(
        (e as Error)?.message === "This account is not an admin."
          ? (e as Error).message
          : "Invalid email or password. Please try again.",
      );
    } finally {
      this.loading.set(false);
    }
  }

  async signInWithPasskey() {
    this.error.set(null);
    this.passkeyLoading.set(true);
    try {
      await this.passkey.signIn();
      this.router.navigate([this.redirectTarget()]);
    } catch (e) {
      this.error.set((e as Error)?.message ?? "Passkey sign-in failed.");
    } finally {
      this.passkeyLoading.set(false);
    }
  }

  private redirectTarget(): string {
    const redirect = this.route.snapshot.queryParamMap.get("redirect");
    return redirect && redirect.startsWith("/") && redirect !== "/login"
      ? redirect
      : "/dashboard";
  }
}
